import _ from 'lodash'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util'
import { Toast } from '@vzmi/shp_shared_compo'
import { PRODUCT_DELIVERY_TYPE, PRODUCT_WARRANTY_STATUS, PROPOSAL_REVIEW_STATUS, PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import { DEFAULT_FORM_DATA, redirectToProposalFeature, isEditableReviewStatus } from '../../Shared'
import errorHelper, { resHandler } from '../../../../../lib/errorHelper'
import { unbindBeforeunload } from '../../Common/BeforeUnload'
import { retrieveRouteByLocation } from '../../../../../lib/i13nUtil'
import { newProposalI13nConf } from '../../../../../../conf/i13nConf'
import { isNewListingProposal } from '../../../../../lib/proposalHelper'
import {
  TOAST_DURATION,
  PROPOSAL_PHASE_TYPE,
  PAGE_FLOW
} from '../../../../constants/proposalTypes'
import {
  getProposalComposePath,
  getCvsQtyLimitMaxByPrice
} from '../../Common/ProposalForm/Shared/util'
import { createErrorToast } from '../../../../lib/FormHelper'
import {
  transformProposalDataToFormData,
  modelsConvert,
  specConverter,
  isNameRepresentExpiringItem
} from './convertors'

const PROPOSAL_PRODUCT_MODEL_VIDEO_URL_PATH = 'product.models[0].videos[0].url'

const fetchClusterList = async (fetchStruDataAttrClusterOptions, params) => {
  const extractPayloadFromRes = res =>
    res && !res.error ? _.get(res, 'payload', []) : []
  try {
    const clusterList = await fetchStruDataAttrClusterOptions(params).then(
      extractPayloadFromRes
    )
    return clusterList
  } catch (err) {
    console.error('fetchClusterList', err)
    throw err
  }
}

const fetchClusterListByCatItem = async (
  fetchStruDataAttrClusterOptions,
  params
) => {
  try {
    const { catItemId, proposalType } = params

    if (!catItemId) {
      console.error('查無可用屬性規格')
      return {}
    }

    const clusterList = await fetchClusterList(
      fetchStruDataAttrClusterOptions,
      {
        categoryId: catItemId,
        proposalType
      }
    )

    return clusterList
  } catch (err) {
    console.error('fetchClusterListByCatItem', err)
    throw err
  }
}

const fetchAttrClusterById = async (
  fetchStruDataAttrClusterOptions,
  params
) => {
  const { id } = params
  try {
    const clusterList = await fetchClusterList(
      fetchStruDataAttrClusterOptions,
      { id }
    )
    const attrCluster = _.get(clusterList, '[0].attrCluster', {})
    return attrCluster
  } catch (err) {
    console.error('fetchAttrClusterById', err)
    throw err
  }
}

const getFormSetting = props => {
  const { setting = {}, proposalType, match, user, subStationOptions } = props

  const matchParams = _.get(match, 'params') || {}
  const phase =
    matchParams.phase === PROPOSAL_PHASE_TYPE.CLONE
      ? PROPOSAL_PHASE_TYPE.COMPOSE
      : matchParams.phase

  return {
    ...setting,
    user,
    subStationOptions,
    showGameContent: false,
    proposalType,
    ...matchParams,
    phase
  }
}

const getDefaultFormData = props => {
  const { subStationOptions, proposalType } = props
  const setting = getFormSetting(props)
  const subStationId = _.get(subStationOptions, '[0].value', null)
  // 供應商登入: 預設帶入小幫手名稱，小幫手可自行修改
  // 模擬登入：預設帶入小幫手名稱，小幫手可自行修改
  // 提案人 PM 仿供應商登入：顯示該 PM 之中文姓名 (不提供修改)
  const defaultFormData = {
    setting,
    subStationId,
    type: proposalType
  }
  const returnFormData = _.merge({}, DEFAULT_FORM_DATA, defaultFormData)
  return returnFormData
}

/**
 * fetch 特定提案單
 *
 * @param {function} actions.fetchProposals
 * @param {number} proposalId
 */
export const getProposalById = async (actions, id, proposalType) => {
  if (!id) throw new Error('proposal id is not provided.')

  try {
    let requestAction
    let params = { id }
    switch (proposalType) {
      case PROPOSAL_TYPE.UPDATE_LISTING_MODEL:
        requestAction = actions.fetchListingModelProposals
        params = { ...params, fields: '+products' }
        break
      case PROPOSAL_TYPE.CLONE_LISTING:
        requestAction = actions.fetchProposals
        params = { ...params, fields: '' }
        break
      default:
        requestAction = actions.fetchProposals
        break
    }
    const proposalPayload = await requestAction(params)
      .then(resHandler)
    return proposalPayload
  } catch (error) {
    console.log('getProposalById 錯誤', error)
    throw error
  }
}

/**
 *  先取得屬性規格才能先預設屬性表格選項，
 * 所以要先抓取並塞入setting，
 * In Place
 */
export const processStruDataAttrCluster = async ({
  fetchStruDataAttrClusterOptions,
  setting,
  proposalData,
  props
}) => {
  const proposalType = _.get(
    setting,
    'proposalType',
    PROPOSAL_TYPE.NEW_LISTING
  )

  const candidateStruDataList = _.get(proposalData, 'candidateStruDataList')

  const productStruDataAttrClusterId = _.get(
    proposalData,
    'product.struDataAttrClusterId',
    null
  )

  if (productStruDataAttrClusterId) {
    let struDataAttrCluster = {}
    let mismatchedCluster = null

    /**
       * subStationId 與 product.subStationId 不一致，
       * 代表 product category path 有異動過，
       */
    const substationsId = _.get(proposalData, 'subStationId', null)
    const productSubstationId = _.get(proposalData, 'product.subStationId', null)
    const hasMismatchedCategoryPath = !!substationsId && !!productSubstationId && substationsId !== productSubstationId

    /**
       * 用 catItemId 去取其下的 strucDataCluster list
       * 再用現有的 struDataAttrClusterId 去對，
       * 對不到的話，就是 strucDataCluster 與子類對不上，
       * 改直接用 struDataAttrClusterId 去打 API 取 attrCluster
       */
    const productCatItemId = _.get(proposalData, 'product.catItemId', null)
    const clusterList = await fetchClusterListByCatItem(
      fetchStruDataAttrClusterOptions,
      {
        proposalType,
        catItemId: productCatItemId
      }
    )
    const matchedCluster = _.find(
      clusterList,
      { value: productStruDataAttrClusterId }
    )
    const hasMismatchedCluster = _.isNil(matchedCluster)

    // 重新選擇路徑
    if (hasMismatchedCategoryPath || hasMismatchedCluster) {
      const phase = _.get(setting, 'phase')
      const isUpdateFlow = !_.get(props, 'submitting')
      if (phase === PROPOSAL_PHASE_TYPE.COMPOSE && isUpdateFlow) {
        /**
           * compose 階段而且不是要前往確認頁階段按下按鈕的時候，
           * struDataAttrCluster 對不上 catItem 的話
           * cat, catItem, struDataAttrCluster 都得要求 user 重新選擇
           */
        _.set(proposalData, 'product.catId', null)
        _.set(proposalData, 'product.catItemId', null)
        _.set(proposalData, 'product.struDataAttrClusterId', null)
      }

      struDataAttrCluster = await fetchAttrClusterById(
        fetchStruDataAttrClusterOptions,
        { id: productStruDataAttrClusterId }
      )

      mismatchedCluster = {
        name: _.get(struDataAttrCluster, 'name', ''),
        id: productStruDataAttrClusterId
      }
    } else {
      struDataAttrCluster = _.get(matchedCluster, 'attrCluster', null)
    }

    _.merge(setting,
      {
        struDataAttrCluster,
        mismatchedCluster
      }
    )
  }

  if (candidateStruDataList) {
    const candidateStruDataClusterList = await Promise.all(
      candidateStruDataList.map(async struData => {
        const { struDataAttrClusterId } = struData
        const struDataAttrCluster = await fetchAttrClusterById(
          fetchStruDataAttrClusterOptions,
          { id: struDataAttrClusterId }
        )

        return struDataAttrCluster
      })
    )

    _.merge(setting, {
      candidateStruDataClusterList
    })
  }

  return {
    setting,
    proposalData
  }
}

/**
 * 依據 proposalType 調整 setting,
 * 之後變複雜的話，可以寫成 switch 或拉出去，
 * In Place
  */
export const processSetting = ({
  setting,
  proposalData
}) => {
  const proposalType = _.get(
    setting,
    'proposalType',
    PROPOSAL_TYPE.NEW_LISTING
  )

  switch (proposalType) {
    case PROPOSAL_TYPE.NEW_LISTING: {
    // 計算單次限購數量(超取)的上限值，並塞到 setting.cvsQtyLimitMax
      const price = _.get(proposalData, 'listing.price', null)
      const cvsQtyLimitMax = getCvsQtyLimitMaxByPrice(price)
      const seoUrl = _.get(proposalData, 'listing.seoUrl', null)
      _.merge(setting,
        {
          // API 取回的  seoUrl 作為 user 輸入值
          userSeoUrl: seoUrl,
          cvsQtyLimitMax
        })
      break
    }
    case PROPOSAL_TYPE.UPDATE_ATTRIBUTE: {
      break
    }
  }

  return {
    setting,
    proposalData
  }
}

export const postLoadFormat = async (props, formValues, proposalData) => {
  try {
    const setting = _.merge({}, getFormSetting(props))

    const { fetchStruDataAttrClusterOptions } = props.actions

    await processStruDataAttrCluster({
      fetchStruDataAttrClusterOptions,
      setting,
      proposalData,
      props
    })

    processSetting({
      setting,
      proposalData
    })

    const finalFormData = transformProposalDataToFormData(setting, proposalData)
    return finalFormData
  } catch (error) {
    console.error('postLoadFormat', error)
    throw error
  }
}

const removeWarehouseProductName = proposalData => {
  const allKeys = []
  const models = _.get(proposalData, 'product.models', [])
  _.forEach(models, (model, modelIndex) => {
    const items = model.items || []
    _.forEach(items, (item, itemIndex) => {
      if (item.warehouseProductName) {
        allKeys.push(`product.models[${modelIndex}].items[${itemIndex}].warehouseProductName`)
      }
    })
  })
  proposalData = _.omit(proposalData, allKeys)
  return proposalData
}

/**
 * 將proposalData.product.models內所有圖片網址整理成Array
 * 並去除重複的網址，以便重新上傳必要的圖片
 */
export const findAllImages = proposal => {
  const imagesInModels = _.flow(
    () => _.get(proposal, 'product.models', []).map(
      model => model.images.map(
        image => image.url)),
    imgeUrls => _.flattenDeep(imgeUrls),
    dedupe => _.uniq(dedupe)
  )()
  return imagesInModels
}

/**
 * 將proposalData.product.models內所有圖片及影片網址
 * 以上傳後的新網址取代
 * @param {Object} transformedProposalData 原提案資料
 * @param {string[]} externalImgUrls 重新上傳前的URL
 * @param {string[]} convertedImgResList 重新上傳後的結果 [{ error, url }]
 */
export const replaceReuploadedImg =
  (transformedProposalData, externalImgUrls, convertedImgResList) => {
    const data = _.cloneDeep(transformedProposalData)
    const isVideos = _.get(data, PROPOSAL_PRODUCT_MODEL_VIDEO_URL_PATH, false)
    const videoUrl = _.get(_.last(convertedImgResList), 'url', '')
    const newModels = _.get(data, 'product.models', []).map(model => ({
      ...model,
      images: model.images.reduce((collect, image) => {
        const reuploadIndex = externalImgUrls.indexOf(image.url)
        const reuploadUrl = _.get(convertedImgResList, `[${reuploadIndex}].url`, '')
        if (!_.isEmpty(reuploadUrl)) {
          collect.push({ ...image, url: reuploadUrl })
        }
        return collect
      }, []),
      ...(isVideos && !_.isEmpty(videoUrl)
        ? {
          videos: [{
            // 若有影片，一定是最後一個URL
            url: videoUrl,
            order: 1
          }]
        }
        : {})
    }))
    return {
      ...data,
      product: {
        ...data.product,
        models: newModels
      }
    }
  }

/**
 * APACSMDESK-61683 處理重新上傳圖片或影片
 * ref https://git.ouryahoo.com/TW-Shopping-BackOffice/component_shopping_nodejs_shared_compo/blob/d8f961/src/MCEEditor/index.js#L256
 * @param {string[]} imgUrls 圖片/影片網址
 * @param {Object} s3services
 * @returns {string[]} 重新上傳後對應的新網址
 */
const handleReuploadImages = (imgUrls, s3services) => {
  const { convertToS3Image, transformS3Object } = s3services
  return Promise.all(imgUrls.map(imgUrl =>
    // download files
    convertToS3Image(imgUrl)
      .then(({ s3FileInfo, error }) => {
        if (!_.isEmpty(error)) {
          throw error
        }

        if (!s3FileInfo) {
          throw new Error(`${imgUrl} 無法取得上傳圖片`)
        }
        // then reupload and get new url
        return transformS3Object(s3FileInfo)
      })
      // extract new url and return
      .then(({ url: internalImgUrl }) => {
        if (!internalImgUrl) {
          throw new Error(`${imgUrl} 重新上傳圖片失敗`)
        }
        return { error: null, url: internalImgUrl }
      })
      .catch(error => {
        return { error, url: '' }
      })
  ))
}

/**
 * 從 fetchedListing 組合 productIds
 * 若為有屬性賣場，會多找出每個實商品的sku
 * @param {Object} fetchedListing
 * @returns {String} productIds
 */
export const getProductIdsForFetchProducts = ({ sku, models }) => {
  // 無屬性賣場 query listing 沒有 models
  if (!models) {
    return `${sku}`
  }
  // 雙層屬性
  if (models[0].items) {
    return `${sku},${
      models.map(({ items }) => items.map(({ sku: itemSku }) => itemSku))
        .join(',')}`
  }
  // 單層屬性
  return `${sku},${
    models.map(({ sku: modelSku }) => modelSku).join(',')
  }`
}

/**
 * [複製賣場] 以指定賣場取得 listing/product query 結果組合成 proposal
 * @param {Object} actions
 * @param {String} listingId 賣場編號
 * @returns converted proposal format data
 */
const getProposalByListingId = async (actions, listingId, s3services) => {
  const {
    fetchSpecificListing,
    fetchProducts
  } = actions
  const fetchedListing = await getProposalById({ fetchProposals: fetchSpecificListing }, listingId, PROPOSAL_TYPE.CLONE_LISTING)
  // 決定fetchProducts的productId, 若賣場有屬性, 會多拿第一個實商品的sku
  const productId = getProductIdsForFetchProducts(fetchedListing)
  const pickedListingFields = [
    'catItemId',
    'deliveryType',
    'featureTitle',
    'isDisplay',
    'isThresholdFreebie',
    'price',
    'seoUrl',
    'preOrderExpectedShipDate',
    'customizedOrderShipDateAfterPlaced',
    'onShelvedTs',
    'offShelvedTs',
    'applyLowGpm',
    'purchaseQtyLimit',
    'cvsPurchaseQtyLimit'
  ]

  const {
    shipType,
    eVoucherType,
    eVoucherAttributes
  } = _.get(fetchedListing, 'skuDetail', {})

  return getProposalById({ fetchProposals: fetchProducts }, productId, PROPOSAL_TYPE.CLONE_LISTING)
    .then(fetchedProducts => ({
      listing: {
        ..._.pick(fetchedListing, pickedListingFields)
      },
      product: {
        // 多屬性商品會多query實商品，product欄位會以它為主(最後一個fetchedProducts.products)
        ...fetchedProducts.products[fetchedProducts.products.length - 1],
        name: fetchedListing.name,
        attributeDisplayMode: fetchedListing.attributeDisplayMode,
        attributes: fetchedListing.productStructuredDataList[0].attributes,
        catItemId: fetchedListing.catItemId,
        catItemName: fetchedListing.catItemName,
        catId: fetchedListing.catId,
        catName: fetchedListing.catName,
        contentRating: fetchedListing.contentRating,
        copy: fetchedListing.copy,
        model: fetchedListing.model,
        models: modelsConvert(fetchedListing, fetchedProducts),
        shortDescription: fetchedListing.shortDescription,
        shareMediaBetweenModels: fetchedListing.shareMediaBetweenModels,
        specs: specConverter(fetchedListing.models),
        struDataAttrClusterId: fetchedListing.productStructuredDataList[0].struDataAttrClusterId,
        // struDataAttrClusterName: '提案的時候不用給',
        warranty: {
          // 預設商品保證狀態為全新產品
          productStatus: PRODUCT_WARRANTY_STATUS.NEW,
          ...fetchedListing.warranty
        },
        isExpiringItem: isNameRepresentExpiringItem(fetchedListing.name),
        shipType,
        eVoucherType,
        eVoucherAttributes,
        brandId: fetchedListing.brandId
      },
      subStationId: fetchedListing.subStationId,
      subStationName: fetchedListing.subStationName,
      type: 'newListing'
    }))
    .then(transformedProposalData => {
      // 處理商品圖及影片重新上傳, 取出所有圖片、影片整理為array, 上傳後用新網址置換
      const video = _.get(transformedProposalData, PROPOSAL_PRODUCT_MODEL_VIDEO_URL_PATH)
      const externalImgUrls = [
        ...findAllImages(transformedProposalData),
        ...(video ? [video] : [])
      ]
      return handleReuploadImages(externalImgUrls, s3services)
        .then(convertedImgResList => {
          const errorMsgList = _.flow(
            () => _.filter(convertedImgResList, convertedImgRes => {
              return _.isEmpty(_.get(convertedImgRes, 'url'))
            }),
            errorResList => _.map(errorResList, errorRes => _.get(errorRes, 'error.message'))
          )()
          if (!_.isEmpty(errorMsgList)) {
            Toast.error(
              <>
                <p>以下檔案上傳失敗：</p>
                {errorMsgList.map((errorMsg, index) => (
                  <p key={index}>{errorMsg}</p>
                ))}
              </>,
              { duration: 30 }
            )
          }
          return replaceReuploadedImg(
            transformedProposalData,
            externalImgUrls,
            convertedImgResList
          )
        })
    })
}

// ProductListingForm get form data
export const getFormData = async props => {
  const {
    silentPageContext,
    proposalType,
    routes,
    location,
    match: {
      params: {
        phase: proposalPhaseByUrl,
        feature
      }
    },
    i13nMethods: { sendI13nPageViewByPageKey, sendI13nPageViewByRoute },
    values = {},
    actions: {
      fetchProposals,
      invokeRedirect,
      ...actions
    },
    convertToS3Image,
    transformS3Object
  } = props

  const queryParams = HistoryUtil.retrieveQueryParams({ location })
  const proposalId = _.get(queryParams, 'id') || _.get(queryParams, 'listingId')
  const isCloneListing = feature === PROPOSAL_TYPE.CLONE_LISTING
  const isFromClone = _.get(queryParams, 'fromClone') === 'true' || isCloneListing

  const currentRouteData = retrieveRouteByLocation({ location, routes })

  let proposalData = {}
  try {
    if (proposalId || proposalId === '') {
      // 複製賣場進行「新增一般賣場提案」者另由getProposalByListingId取得資料並轉成proposal
      proposalData = isCloneListing
        ? await getProposalByListingId(actions, proposalId, { convertToS3Image, transformS3Object })
        : await getProposalById({ fetchProposals }, proposalId)

      const currentCategoryId = _.get(proposalData, 'subStationId')
      const currentDeliveryType = _.get(proposalData, 'listing.deliveryType')
      const deliveryTypeMap = _.flow(
        () => [
          // user 允許的交貨期限
          ..._.get(props, 'page.user.profile.toggles', []),
          // 賣場、提案選擇的子站允許的交貨期限
          ..._.flow(
            () => _.get(props, 'proposal.subStationCategories', []),
            subStationCategories => _.find(subStationCategories, { categoryId: currentCategoryId }),
            subStationCategory => _.get(subStationCategory, 'deliveryTypes', [])
          )()
        ],
        _.keyBy
      )()
      // 因為權限會隨著時間變動，所以複製賣場、提案時交貨期限需要重新判斷既有選項跟著調整
      // 如果目前的交貨期限在 user profile & substation 允許裡則沿用
      // 否則預設改成正常交貨期(normal)
      const validDeliveryType = (currentDeliveryType in deliveryTypeMap)
        ? currentDeliveryType
        : PRODUCT_DELIVERY_TYPE.NORMAL
      _.set(proposalData, 'listing.deliveryType', validDeliveryType)
      if (validDeliveryType !== PRODUCT_DELIVERY_TYPE.PRE_ORDER) {
        _.set(proposalData, 'listing.preOrderExpectedShipDate', null)
      }
      /**
       * URL, 與 fetchedProposal 的 proposalType 不一致時，
       * 導回正確 proposalType 的頁面，phase 不變
       */
      let { type: fetchedProposalType } = proposalData

      // 轉換 proposal type
      // 目前如: newListingByApi => newListing
      fetchedProposalType = isNewListingProposal(fetchedProposalType)
        ? PROPOSAL_TYPE.NEW_LISTING
        : fetchedProposalType

      if (fetchedProposalType !== proposalType) {
        redirectToProposalFeature(
          invokeRedirect,
          fetchedProposalType,
          proposalPhaseByUrl,
          proposalId
        )
      } else {
        switch (proposalPhaseByUrl) {
          case PROPOSAL_PHASE_TYPE.COMPOSE: {
            if (isFromClone) {
              // 複製提案單時，需移除提案編號
              proposalData = _.omit(proposalData, [
                'id',
                'modifier',
                'creator',
                'executeStatus',
                'executeResult',
                'declineReason',
                'declineDetail',
                'suggestion'
              ])
              // 複製提案單時，需清除使用者自訂的進倉欄位名稱
              proposalData = removeWarehouseProductName(proposalData)
              _.set(
                proposalData,
                'reviewStatus',
                PROPOSAL_REVIEW_STATUS.COMPOSING
              )
              // 複製提案視為新增提案，將轉換後的 type 覆蓋原 type
              _.set(proposalData, 'type', fetchedProposalType)
              // clear query string
              if (!isCloneListing) {
                window.history.replaceState(
                  {},
                  'compose page',
                  getProposalComposePath(proposalType)
                )
              }
              // 紀錄複製提案 GA
              sendI13nPageViewByPageKey(
                `${proposalType}-${
                  isCloneListing ? PROPOSAL_TYPE.CLONE_LISTING : PROPOSAL_PHASE_TYPE.CLONE
                }`,
                newProposalI13nConf
              )
            }

            const { reviewStatus } = proposalData || {}
            const isEditableProposal = isEditableReviewStatus(reviewStatus)
            if (!isEditableProposal) {
              Toast.error('提案單狀態不允許編輯，僅供檢視', {
                duration: TOAST_DURATION
              })
              const currentUrl = window.location.href
              const overviewUrl = currentUrl.replace(
                RegExp(`${PROPOSAL_PHASE_TYPE.COMPOSE}`),
                `${PROPOSAL_PHASE_TYPE.OVERVIEW}`
              )

              // 不允許編輯之提案單，跳轉檢視頁及不回傳提案資訊
              unbindBeforeunload()
              errorHelper.redirect({ url: overviewUrl })
              proposalData = {}
            }

            // send EDIT first PageView
            sendI13nPageViewByPageKey(`${proposalType}-${PAGE_FLOW.EDIT}`, newProposalI13nConf)
            break
          }
          case PROPOSAL_PHASE_TYPE.PREVIEW: {
            sendI13nPageViewByRoute(currentRouteData, newProposalI13nConf)
            break
          }
          case PROPOSAL_PHASE_TYPE.OVERVIEW: {
            const { setSilentPageTo } = silentPageContext
            setSilentPageTo(true)
            unbindBeforeunload()
            _.set(proposalData, 'setting.readOnly', true)
            // check warranty is empty
            const isWarrantyEmpty = _.isUndefined(
              _.get(proposalData, 'product.warranty')
            )
            _.set(proposalData, 'product.isWarrantyEmpty', isWarrantyEmpty)
            // send OVERVIEW first PageView
            sendI13nPageViewByRoute(currentRouteData, newProposalI13nConf)
            break
          }
          default:
            break
        }
      }
    } else if (proposalPhaseByUrl === PROPOSAL_PHASE_TYPE.COMPOSE) {
      sendI13nPageViewByRoute(currentRouteData, newProposalI13nConf)
    }
    // transform proposal data from api and parse it to form data
    const setting = getFormSetting(props)
    const formValues = _.merge({}, values, { setting })
    const formData = await postLoadFormat(
      props,
      formValues,
      _.merge({ setting }, getDefaultFormData(props), proposalData)
    )
    return formData
  } catch (error) {
    console.log('查詢提案單錯誤', error)
    const message = isCloneListing
      ? '複製賣場資訊發生錯誤，請再試一次'
      : '查詢提案單錯誤'
    createErrorToast({ message, error })
    throw error
  }
}

/**
 * 取得賣場是無屬性0、單層屬性1、雙層屬性2賣場
 * @param {object} listing 賣場
 */
export const getAttrLevelFromListing = listing => {
  const itemSpec = _.get(listing, 'models[0].items[0].spec')
  if (itemSpec) {
    return 2
  }

  const spec = _.get(listing, 'models[0].spec')
  if (spec) {
    return 1
  }

  return 0
}

/**
 * 取得商品是無屬性0、單層屬性1、雙層屬性2商品
 * @param {object} product 賣場
 */
export const getAttrLevelFromProduct = product => {
  if (_.get(product, 'parentSpec')) {
    return 2
  }

  if (_.get(product, 'spec')) {
    return 1
  }

  return 0
}
